import React, { useState, useCallback } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormInput, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilPlus, cilSearch, cilPencil } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import Select from 'react-select';
import { useSupportCategories, useInactiveSupportCategories } from 'src/hooks/useSupportCategories';

const urlApi = '/qa';
const urlRouter = '/qa';
const itemsPerPage = 30;

export async function LoaderQa() {
	return AxiosInstance.get(urlApi, { params: { page: 1, itemsPerPage } });
}

const Qa = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();
	const activeCategories = useSupportCategories();
	const inactiveCategories = useInactiveSupportCategories();

	const [rows, setRows] = useState(loaderData?.data?.data ?? []);
	const [rowCountState, setRowCountState] = useState(loaderData?.data?.total ?? 0);
	const [searchText, setSearchText] = useState('');
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [showLoader, setShowLoader] = useState(false);
	const [page, setPage] = useState(0);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const getRowsTable = (p = 1, search = searchText, cats = selectedCategories) => {
		setShowLoader(true);
		const params = { page: p, itemsPerPage };
		if (search) params.search = search;
		if (cats.length > 0) params.category_ids = cats.map(c => c.value);
		AxiosInstance.get(urlApi, { params }).then(response => {
			setRows(response.data.data ?? []);
			setRowCountState(response.data.total ?? 0);
			setShowLoader(false);
		}).catch(() => setShowLoader(false));
	};

	const handleSearch = () => {
		setPage(0);
		getRowsTable(1);
	};

	const handleClearFilters = () => {
		setSearchText('');
		setSelectedCategories([]);
		setPage(0);
		getRowsTable(1, '', []);
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
		getRowsTable(newPage + 1);
	};

	const handleExportCsv = () => {
		const header = ['ID', 'Питання', 'Відповідь', 'Категорії', 'Активна'];
		const escapeCell = (val) => '"' + String(val ?? '').replace(/"/g, '""') + '"';
		const csvRows = rows.map(r => [
			r.id,
			escapeCell(r.question),
			escapeCell(r.answer),
			escapeCell((r.categories || []).map(c => c.name).join(', ')),
			r.is_active ? 'Так' : 'Ні',
		].join(','));
		const csvContent = '﻿' + [header.join(','), ...csvRows].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = 'qa_export.csv';
		link.click();
		URL.revokeObjectURL(url);
	};

	const categoryOptions = [{ value: -1, label: 'Без категорії' }, ...activeCategories.map(c => ({ value: c.id, label: c.name }))];

	const columns = [
		{ field: 'id', headerName: 'ID', width: 70 },
		{
			field: 'question', headerName: 'Питання', width: 350,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className="table-name-link" title={params.row.question}>
					{params.row.question?.length > 60 ? params.row.question.substring(0, 60) + '…' : params.row.question}
				</Link>
			),
		},
		{
			field: 'categories', headerName: 'Категорії', width: 200, sortable: false,
			renderCell: (params) => {
				const cats = params.row.categories || [];
				if (!cats.length) return <span>—</span>;
				const parts = cats.map(cat => {
					const active = activeCategories.find(c => c.id === cat.id);
					if (active) return { name: cat.name, inactive: false };
					const inactive = inactiveCategories.find(c => c.id === cat.id);
					return { name: inactive?.name || cat.name, inactive: true };
				});
				return (
					<span title={parts.map(p => p.name).join(', ')}>
						{parts.map((p, i) => (
							<React.Fragment key={i}>
								{i > 0 && ', '}
								{p.inactive
									? <span style={{ textDecoration: 'line-through', color: '#999' }}>{p.name}</span>
									: p.name
								}
							</React.Fragment>
						))}
					</span>
				);
			},
		},
		{
			field: 'is_active', headerName: 'Активна', width: 80,
			renderCell: (params) => (
				<CBadge color={params.row.is_active ? 'success' : 'secondary'}>
					{params.row.is_active ? 'Активна' : 'Неактивна'}
				</CBadge>
			),
		},
		{
			field: 'action', headerName: 'Переглянути', headerAlign: 'center', align: 'center', sortable: false, width: 105,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className='custom-view-icon' title="Переглянути запис">
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		{
			field: 'action_edit', headerName: 'Редагувати', headerAlign: 'center', align: 'center', sortable: false, width: 95,
			renderCell: (params) => (
				<Link to={`${urlRouter}/update/${params.row.id}`} className='custom-edit-icon' title="Редагувати запис">
					<CIcon icon={cilPencil} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
	];

	return (
		<CRow>
			<CCol xs={12}>
				<CCard className="mb-4">
					<CAccordion flush>
						<CAccordionItem>
							<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
							<CAccordionBody>
								<CRow className="my-3 mx-2">
									<CCol md={4} className="filter-field">
										<CFormInput
											placeholder="Пошук по тексту"
											value={searchText}
											onChange={(e) => setSearchText(e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4} className="filter-field">
										<Select
											isMulti
											options={categoryOptions}
											value={selectedCategories}
											onChange={setSelectedCategories}
											placeholder="Категорії..."
											isClearable
											classNamePrefix="react-select"
										/>
									</CCol>
									<CCol md={4} className="filter-field" style={{ textAlign: 'end' }}>
										<CButton color="primary" className="me-2" onClick={handleSearch} title="Застосувати фільтри">
											Шукати
										</CButton>
										<CButton color="secondary" variant="outline" onClick={handleClearFilters} title="Очистити всі фільтри">
											Очистити фільтри
										</CButton>
									</CCol>
								</CRow>
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>
					<CCardHeader><strong>База знань (Q&amp;A)</strong></CCardHeader>
					<CCardBody>
						<p className="text-medium-emphasis small" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
							<Link to={`${urlRouter}/create`}>
								<CButton color="secondary" title="Створити новий запис Q&A">
									<CIcon icon={cilPlus} customClassName="nav-icon" height={15} />Створити
								</CButton>
							</Link>
							<CButton color="secondary" variant="outline" onClick={handleExportCsv} title="Експортувати поточну сторінку у CSV">
								Експорт у CSV
							</CButton>
						</p>
						<div style={{ height: 600, width: '100%' }}>
							<DataGrid
								rows={rows}
								columns={columns}
								page={page}
								pageSize={itemsPerPage}
								rowsPerPageOptions={[itemsPerPage]}
								pagination
								paginationMode="server"
								rowCount={rowCountState}
								loading={showLoader}
								onPageChange={handlePageChange}
								initialState={{
									sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
								}}
							/>
						</div>
						<div className="d-flex justify-content-start mt-3">
							<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
								Повернутись
							</CButton>
						</div>
					</CCardBody>
				</CCard>
			</CCol>
		</CRow>
	);
};

export default Qa;
